export interface Element {
  atomicNumber: number
  symbol: string
  name: string
  atomicWeight: string
  category: string
  group: string
  state: string
  discoveryYear: string
  color: string
  description?: string
}
export interface ElementGroup {
  name: string
  description: string
  elements: Element[]
}
export const elementGroups: Record<string, ElementGroup> = {
  alkali_metals: {
    name: 'Alkali Metals',
    description:
      'Alkali metals are highly reactive metals that form alkaline solutions when they react with water. They are characterized by having one electron in their outer shell, which they readily give up to form positive ions.',
    elements: [
      {
        atomicNumber: 3,
        symbol: 'Li',
        name: 'Lithium',
        atomicWeight: '6.94',
        category: 'Alkali Metal',
        group: 'Group 1',
        state: 'Solid',
        discoveryYear: '1817',
        color: '#CC80CC',
        description: 'Used in batteries, ceramics, and pharmaceuticals.',
      },
      {
        atomicNumber: 11,
        symbol: 'Na',
        name: 'Sodium',
        atomicWeight: '22.990',
        category: 'Alkali Metal',
        group: 'Group 1',
        state: 'Solid',
        discoveryYear: '1807',
        color: '#CC80CC',
        description: 'Essential for nerve and muscle function in animals.',
      },
      {
        atomicNumber: 19,
        symbol: 'K',
        name: 'Potassium',
        atomicWeight: '39.098',
        category: 'Alkali Metal',
        group: 'Group 1',
        state: 'Solid',
        discoveryYear: '1807',
        color: '#CC80CC',
        description: 'Essential for plant growth and cellular function.',
      },
      {
        atomicNumber: 37,
        symbol: 'Rb',
        name: 'Rubidium',
        atomicWeight: '85.468',
        category: 'Alkali Metal',
        group: 'Group 1',
        state: 'Solid',
        discoveryYear: '1861',
        color: '#CC80CC',
        description: 'Used in atomic clocks and electronics.',
      },
    ],
  },
  alkaline_earth_metals: {
    name: 'Alkaline Earth Metals',
    description:
      'Alkaline earth metals are silvery, shiny, and relatively reactive at standard temperature and pressure. They readily lose their two outermost electrons to form cations with a 2+ charge.',
    elements: [
      {
        atomicNumber: 4,
        symbol: 'Be',
        name: 'Beryllium',
        atomicWeight: '9.0122',
        category: 'Alkaline Earth Metal',
        group: 'Group 2',
        state: 'Solid',
        discoveryYear: '1797',
        color: '#FFBC00',
        description: 'Used in aerospace and nuclear applications.',
      },
      {
        atomicNumber: 12,
        symbol: 'Mg',
        name: 'Magnesium',
        atomicWeight: '24.305',
        category: 'Alkaline Earth Metal',
        group: 'Group 2',
        state: 'Solid',
        discoveryYear: '1755',
        color: '#FFBC00',
        description:
          'Essential for chlorophyll in plants and many enzymes in humans.',
      },
      {
        atomicNumber: 20,
        symbol: 'Ca',
        name: 'Calcium',
        atomicWeight: '40.078',
        category: 'Alkaline Earth Metal',
        group: 'Group 2',
        state: 'Solid',
        discoveryYear: '1808',
        color: '#FFBC00',
        description: 'Essential for bones, teeth, and cellular functions.',
      },
    ],
  },
  transition_metals: {
    name: 'Transition Metals',
    description:
      'Transition metals are characterized by partially filled d orbitals, giving them unique properties such as multiple oxidation states and colorful compounds. They are generally hard, dense, and good conductors of heat and electricity.',
    elements: [
      {
        atomicNumber: 26,
        symbol: 'Fe',
        name: 'Iron',
        atomicWeight: '55.845',
        category: 'Transition Metal',
        group: 'Group 8',
        state: 'Solid',
        discoveryYear: 'Ancient',
        color: '#E06633',
        description:
          'Essential for hemoglobin and widely used in construction.',
      },
      {
        atomicNumber: 29,
        symbol: 'Cu',
        name: 'Copper',
        atomicWeight: '63.546',
        category: 'Transition Metal',
        group: 'Group 11',
        state: 'Solid',
        discoveryYear: 'Ancient',
        color: '#E06633',
        description:
          'Excellent electrical conductor used in wiring and electronics.',
      },
      {
        atomicNumber: 47,
        symbol: 'Ag',
        name: 'Silver',
        atomicWeight: '107.87',
        category: 'Transition Metal',
        group: 'Group 11',
        state: 'Solid',
        discoveryYear: 'Ancient',
        color: '#E06633',
        description:
          'Highest electrical and thermal conductivity of any element.',
      },
      {
        atomicNumber: 79,
        symbol: 'Au',
        name: 'Gold',
        atomicWeight: '196.97',
        category: 'Transition Metal',
        group: 'Group 11',
        state: 'Solid',
        discoveryYear: 'Ancient',
        color: '#E06633',
        description:
          'Highly valued precious metal used in jewelry and electronics.',
      },
    ],
  },
  nonmetals: {
    name: 'Nonmetals',
    description:
      'Nonmetals are elements that generally lack metallic attributes. They are typically poor conductors of heat and electricity, and tend to gain or share electrons when they react with other elements.',
    elements: [
      {
        atomicNumber: 1,
        symbol: 'H',
        name: 'Hydrogen',
        atomicWeight: '1.008',
        category: 'Nonmetal',
        group: 'Group 1',
        state: 'Gas',
        discoveryYear: '1766',
        color: '#8AFF8A',
        description: 'Most abundant element in the universe.',
      },
      {
        atomicNumber: 6,
        symbol: 'C',
        name: 'Carbon',
        atomicWeight: '12.011',
        category: 'Nonmetal',
        group: 'Group 14',
        state: 'Solid',
        discoveryYear: 'Ancient',
        color: '#8AFF8A',
        description: 'Basis of organic chemistry and life on Earth.',
      },
      {
        atomicNumber: 7,
        symbol: 'N',
        name: 'Nitrogen',
        atomicWeight: '14.007',
        category: 'Nonmetal',
        group: 'Group 15',
        state: 'Gas',
        discoveryYear: '1772',
        color: '#8AFF8A',
        description: "Makes up about 78% of Earth's atmosphere.",
      },
      {
        atomicNumber: 8,
        symbol: 'O',
        name: 'Oxygen',
        atomicWeight: '15.999',
        category: 'Nonmetal',
        group: 'Group 16',
        state: 'Gas',
        discoveryYear: '1774',
        color: '#8AFF8A',
        description: 'Essential for respiration in most living organisms.',
      },
    ],
  },
  noble_gases: {
    name: 'Noble Gases',
    description:
      'Noble gases are colorless, odorless, monatomic gases with very low chemical reactivity. They have complete valence electron shells, which make them extremely stable and unlikely to form compounds.',
    elements: [
      {
        atomicNumber: 2,
        symbol: 'He',
        name: 'Helium',
        atomicWeight: '4.0026',
        category: 'Noble Gas',
        group: 'Group 18',
        state: 'Gas',
        discoveryYear: '1868',
        color: '#80FFFF',
        description:
          'Second most abundant element in the universe after hydrogen.',
      },
      {
        atomicNumber: 10,
        symbol: 'Ne',
        name: 'Neon',
        atomicWeight: '20.180',
        category: 'Noble Gas',
        group: 'Group 18',
        state: 'Gas',
        discoveryYear: '1898',
        color: '#80FFFF',
        description:
          'Used in signs and produces a distinctive reddish-orange glow.',
      },
      {
        atomicNumber: 18,
        symbol: 'Ar',
        name: 'Argon',
        atomicWeight: '39.948',
        category: 'Noble Gas',
        group: 'Group 18',
        state: 'Gas',
        discoveryYear: '1894',
        color: '#80FFFF',
        description: "Third most common gas in Earth's atmosphere.",
      },
    ],
  },
  halogens: {
    name: 'Halogens',
    description:
      'Halogens are highly reactive nonmetallic elements that readily form salts with metals. They are characterized by having seven electrons in their outer shell, making them just one electron short of a full octet.',
    elements: [
      {
        atomicNumber: 9,
        symbol: 'F',
        name: 'Fluorine',
        atomicWeight: '18.998',
        category: 'Halogen',
        group: 'Group 17',
        state: 'Gas',
        discoveryYear: '1886',
        color: '#FFFF00',
        description: 'Most electronegative and reactive of all elements.',
      },
      {
        atomicNumber: 17,
        symbol: 'Cl',
        name: 'Chlorine',
        atomicWeight: '35.45',
        category: 'Halogen',
        group: 'Group 17',
        state: 'Gas',
        discoveryYear: '1774',
        color: '#FFFF00',
        description: 'Used in water purification and as a disinfectant.',
      },
      {
        atomicNumber: 35,
        symbol: 'Br',
        name: 'Bromine',
        atomicWeight: '79.904',
        category: 'Halogen',
        group: 'Group 17',
        state: 'Liquid',
        discoveryYear: '1826',
        color: '#FFFF00',
        description:
          'One of only two elements that are liquid at room temperature.',
      },
    ],
  },
}
